import { useEffect, useMemo } from 'react';
import { useContextDeploymentInfo } from '../../../hooks/useContextDeploymentInfo';
import { getDeploymentRegenerateUrl } from '../../../routes';
import {
  RegenerateRedirectProps,
  RegenerationErrorType,
} from '../../../types/types';
import { getErrorMessage, logError } from '../../../utils/errorUtils';
import { RegenerationLoading } from './RegenerationLoading';

export const RegenerateRedirect = ({
  sitecoreContextId,
  resources,
  applicationsNodes,
  onRedirectError,
}: RegenerateRedirectProps) => {
  const { projectId, environmentId, isLoading, error } =
    useContextDeploymentInfo(sitecoreContextId, resources, applicationsNodes);

  // Optimized organization ID extraction - try multiple sources
  const organizationId = useMemo(() => {
    // 1. URL search params (highest priority)
    const searchParams = new URLSearchParams(window.location.search);
    const orgIdFromParams = searchParams.get('organization');
    if (orgIdFromParams) {
      return orgIdFromParams;
    }

    // 2. URL path segments
    const pathParts = window.location.pathname.split('/');
    for (let i = 0; i < pathParts.length; i++) {
      if (pathParts[i] === 'organization' && i + 1 < pathParts.length) {
        return pathParts[i + 1];
      }
    }

    // 3. Resources data
    if (resources && resources.length > 0) {
      const contextResource = resources.find((r) => r.organizationId);
      if (contextResource?.organizationId) {
        return contextResource.organizationId;
      }
    }

    // 4. Browser storage fallback
    return (
      localStorage.getItem('organizationId') ||
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('organizationId='))
        ?.split('=')[1] ||
      null
    );
  }, [resources]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Consolidated error handling - determine error type and message
    if (error || !projectId || !environmentId || !organizationId) {
      let errorType = RegenerationErrorType.GENERAL_ERROR;
      let errorDetails = '';

      if (!organizationId) {
        errorType = RegenerationErrorType.MISSING_ORGANIZATION_ID;
      } else if (!projectId && !environmentId) {
        errorType = RegenerationErrorType.NO_ANNOTATIONS;
      } else if (!projectId) {
        errorType = RegenerationErrorType.MISSING_PROJECT_ID;
      } else if (!environmentId) {
        errorType = RegenerationErrorType.MISSING_ENVIRONMENT_ID;
      } else if (error) {
        errorType = RegenerationErrorType.GENERAL_ERROR;
        errorDetails = error.message;
        logError('RegenerateRedirect', error, { sitecoreContextId });
      }

      const errorMessage = getErrorMessage(errorType, errorDetails);
      onRedirectError(errorMessage);
      return;
    }

    // Success path - redirect to deployment URL
    const formattedOrgId = organizationId.startsWith('org_')
      ? organizationId
      : 'org_' + organizationId;

    const deployUrl = getDeploymentRegenerateUrl(
      projectId,
      environmentId,
      formattedOrgId,
    );
    window.location.href = deployUrl;
  }, [
    sitecoreContextId,
    projectId,
    environmentId,
    isLoading,
    error,
    onRedirectError,
    organizationId,
  ]);

  if (isLoading) {
    return (
      <RegenerationLoading
        isLoading={true}
        loadingMessage='Validating regeneration requirements...'
      />
    );
  }

  return null;
};
